import AddIcon from 'icons/add';
import MinusIcon from 'icons/minus';
import {
  fixedDecimalPlaces,
  numberAdd,
  numberDivide,
  numberMinus,
  numberMutiply2,
} from 'libs/compute';
import { MAX_QUANTITY } from 'libs/cons';
import { noop } from 'libs/utils';
import numeral from 'numeral';
import { useEffect, useMemo, useState } from 'react';

type ComputeType = 'add' | 'minus';

type StepperProps = {
  value: number | string;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  decimalLength?: number;
  inputWidth?: string;
  inputHeight?: string;
  suffix?: React.ReactNode;
  suffixPre?: React.ReactNode;
  readonly?: boolean;
  disabled?: boolean;
  disableMinus?: boolean;
  disableAdd?: boolean;
  onChange?: Function;
  keepDecimalOnChange?: boolean;
  notifyWhenInputChange?: boolean;
  showMinus?: boolean;
  showAdd?: boolean;
  placeholder?: string;
  onExceed?: Function; // 超出最大值 callback
  onBlur?: Function;
  autoFix?: boolean;
};

export default function Stepper({
  value,
  defaultValue = 1,
  min = 1,
  max = MAX_QUANTITY,
  step = 1,
  integer = true,
  decimalLength,
  inputWidth,
  inputHeight = '22px',
  suffix,
  suffixPre,
  readonly,
  disabled,
  disableMinus,
  disableAdd,
  onChange = noop,
  keepDecimalOnChange,
  notifyWhenInputChange,
  showMinus = true,
  showAdd = true,
  placeholder,
  onExceed = noop,
  onBlur = noop,
  autoFix = false,
}: StepperProps) {
  const [current, setCurrent] = useState<number | string>('');

  useEffect(() => {
    setCurrent(value || '');
  }, [value]);

  const inputStyle = useMemo(
    () => ({
      width: inputWidth,
    }),
    [inputWidth],
  );
  const minusDisabled = useMemo(
    () => disabled || disableMinus || +current <= (min > 1 ? min : 1),
    [disabled, disableMinus, current, min],
  );
  const addDisabled = useMemo(
    () => disabled || disableAdd || +current >= max,
    [disabled, disableAdd, current, max],
  );

  function autoFixVal(value, step) {
    if (autoFix && step !== 1) {
      let v: any = numeral(numberMutiply2(Math.floor(+numberDivide(+value, +step)), step)).format(
        '0.[0000]',
      );

      if (+value <= min || +v === 0) {
        v = step;
      }
      return v;
    } else {
      return value;
    }
  }

  function handleCompute(type: ComputeType) {
    // 小于1且被1整除， 步长为1
    const formatStep = Math.max(1, step) === 1 && 10 ** 4 % (step * 10 ** 4) === 0 ? 1 : step;
    let value: number | string =
      type === 'add' ? numberAdd(current, formatStep) : numberMinus(current, formatStep);

    if (+value >= max) {
      +value > max && onExceed?.();
      value = max;
    }
    if (+value <= min) {
      value = min;
    }

    value = autoFixVal(value, formatStep);

    if (!keepDecimalOnChange) {
      // + operator will trim trailing 0
      setCurrent(+value);
      onChange?.(+value);
    } else {
      setCurrent(value);
      onChange?.(value);
    }
  }

  function formatVal(value) {
    const splitVal = value.toString().split('.');
    return splitVal.length > 1
      ? `${splitVal[0]}.${splitVal[1]?.slice(0, decimalLength)}`
      : splitVal[0];
  }

  function hanldeInputChange(event) {
    const curInput = event.target as HTMLInputElement;
    const { value } = curInput;
    let currentVal: number = min;
    if (integer) {
      const inputVal = parseInt(value);
      if (!isNaN(inputVal) || inputVal > min) {
        currentVal = inputVal;
        if (inputVal >= max) {
          inputVal > max && onExceed?.();
          currentVal = max;
        }
      }
      setCurrent(currentVal);
    } else {
      notifyWhenInputChange && onChange(value);
      setCurrent(value);
    }
  }
  function handleInputBlur(event) {
    const curInput = event.target as HTMLInputElement;
    const { value } = curInput;
    if (!value) {
      setCurrent(min);
      onChange?.(min);
      onBlur?.(min);
      return;
    }
    let inputVal: number | string;
    if (integer) {
      inputVal = parseInt(value);
      if (isNaN(inputVal)) {
        setCurrent(min);
        onChange?.(min);
        onBlur?.(min);
        return;
      }
    } else {
      inputVal = parseFloat(value);
      if (isNaN(inputVal)) {
        setCurrent(min);
        onChange?.(min);
        onBlur?.(min);
        return;
      }
      if (decimalLength) {
        inputVal = +formatVal(inputVal);

        if (!keepDecimalOnChange) {
          if (inputVal <= min) {
            inputVal = min;
          }
          if (inputVal >= max) {
            inputVal > max && onExceed?.();
            inputVal = max;
          }
        } else {
          // keep traling 0s
          inputVal = fixedDecimalPlaces(inputVal, decimalLength);
        }
      }
    }

    inputVal = autoFixVal(inputVal, step);

    setCurrent(inputVal);
    onChange?.(inputVal);
    onBlur?.(inputVal);
  }

  return (
    <div className="stepper">
      {showMinus && (
        <button
          className="btn"
          aria-label="minus"
          disabled={minusDisabled}
          onClick={(event) => {
            event.preventDefault();
            handleCompute('minus');
          }}
        >
          <MinusIcon />
        </button>
      )}
      <input
        value={current}
        type={integer ? 'tel' : 'text'}
        className="stepper-input"
        placeholder={placeholder}
        style={inputStyle}
        readOnly={readonly}
        disabled={disabled}
        onChange={hanldeInputChange}
        onBlur={handleInputBlur}
      />
      {suffixPre && <div className="stepper-suffix">{suffixPre}</div>}
      {showAdd && (
        <button
          className="btn"
          aria-label="add"
          disabled={addDisabled}
          onClick={(event) => {
            event.preventDefault();
            handleCompute('add');
          }}
        >
          <AddIcon />
        </button>
      )}

      {suffix}
      <style jsx>{`
        .stepper {
          display: flex;
          flex-direction: row;
          align-items: center;
          position: relative;
          height: ${inputHeight};
        }
        .btn {
          width: 22px;
          height: ${inputHeight};
          display: flex;
          justify-content: center;
          align-items: center;
          border: none;
          outline: none;
          border-radius: 3px;
          background-color: transparent;
        }
        .btn:active {
          background-color: #f7f7f7;
        }
        .btn:disabled {
          opacity: 0.3;
        }
        .stepper-input {
          width: 42px;
          height: ${inputHeight};
          padding: 0 3px;
          font-size: 13px;
          color: #2b2b2b;
          font-weight: 500;
          text-align: center;
          border: none;
          outline: none;
          background: #f7f7f7;
          border-radius: 3px;
        }
        .stepper-input::-webkit-input-placeholder {
          font-size: 12px;
          color: #bbb;
        }
        .stepper-suffix {
          width:28px;
          height: ${inputHeight};
          line-height: ${inputHeight};
          color: #2b2b2b;
          background: #f7f7f7;
          margin: 0px;
        }
      `}</style>
    </div>
  );
}
